import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { type SendMessageContextParams } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { populateSendMessageContext } from './populateSendMessageContext';
import type { ChatStoreContainerProps } from './types';

/**
 * Build the request context that `sendMessageConfirmedTools` sends. Wraps the private
 * `populateSendMessageContext` and narrows its result to the wire-payload shape
 * (`SendMessageContextParams`) the service accepts. Exported (instead of exporting
 * `populateSendMessageContext` directly) so the return type stays simple under
 * `--isolatedDeclarations` and `sendMessage`'s own typing is unaffected. `aiFeatureContext`
 * is intentionally omitted — the store keeps that gated by `includeAIFeatureContext`.
 */
export const populateConfirmedToolsContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	isEmptyStagingArea?: boolean,
): Promise<Partial<SendMessageContextParams>> => {
	const context = await populateSendMessageContext(
		chatContextStoreActions,
		isEmptyStagingArea || isFirstPrompt,
		undefined,
		undefined,
		addCurrentPageAsContextUserPreference,
		contentContextValue,
		isEmptyStagingArea ? { experience: 'cwr' } : undefined,
		productKey,
		sendAnalyticsEvent,
	);
	return {
		editorContext: context.editorContext,
		whiteboardContext: context.whiteboardContext,
		slidesContext: context.slidesContext,
		databaseContext: context.databaseContext,
		dashboardEditorContext: context.dashboardEditorContext,
		browserContext: context.browserContext,
		additionalContext: context.additionalContext,
		jiraWorkflowContext: context.jiraWorkflowContext,
		dashboardContext: context.dashboardContext,
		useCurrentBrowsingContext: context.useCurrentBrowsingContext,
	};
};
